import { CollectionConst } from "constants/document/CollectionConst";
import { BankerRequiredFieldList } from "presentation/constant/Banker/BankerConstant";
import { RefundConstant, RefundPermissionCodePolicy } from "presentation/constant/Refund/RefundConstant";
import { useRefundVM } from "presentation/hook/Refund/useRefundVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useRefundTracked } from "presentation/store/Refund/RefundProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import DatePickerComponent from "presentation/view/components/DatePickerComponent";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import { NbisIconButton } from "presentation/view/components/NbisIconButton";
import NumberInputComponent from "presentation/view/components/NumberInputComponent";
import { useCallback, useMemo, useState } from "react";
import { FieldType, HPHBreadcrumb, IconButton, IFieldValue, Loader } from "veronica-ui-component/dist/component/core";
import { Breadcrumb, SidebarCaption, Sidebarheader, SidebarTitle, StyledAction, StyledSidebar } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const RefundEditPanel = () => {
    const SCREEN_CONSTANT = RefundConstant;
    const [refundState] = useRefundTracked();
    const refundVM = useRefundVM();
    const { currentRow, currentEditRow, searchCriteria, isAdd, isEdit, masterState } = refundState;
    const { allFormState, isSaveClicked } = masterState;
    const [isLoading, setIsLoading] = useState(false);
    const messageBarVM = useMessageBarVM();
    const [anaInfoState] = useANAInfoTracked();
    const { allowUpdate, operatingUnit } = anaInfoState;

    const isStatusOperable = useCallback((operation: string | null, status: string) => {
        let ret = true;
        if (RefundPermissionCodePolicy.REFUND_BUTTON_VERIFY === operation) {
            if (CollectionConst.refundStatus.ITEM_INPUT !== status) {
                messageBarVM.showError("Cannot " + operation + " because the refund claim(s) is not in " + CollectionConst.refundStatus.ITEM_INPUT + " status.");
                ret = false;
            }
        } else if (RefundPermissionCodePolicy.REFUND_BUTTON_APPROVE === operation) {
            if (CollectionConst.refundStatus.ITEM_VERIFIED !== status) {
                messageBarVM.showError("Cannot " + operation + " because the refund claim(s) is not in " + CollectionConst.refundStatus.ITEM_VERIFIED + " status.");
                ret = false;
            }
        } else if (RefundPermissionCodePolicy.REFUND_BUTTON_REJECT === operation) {
            if (CollectionConst.refundStatus.ITEM_VERIFIED !== status
                && CollectionConst.refundStatus.ITEM_APPROVED !== status) {
                messageBarVM.showError("Cannot " + operation + " because the refund claim(s) is not in " + CollectionConst.refundStatus.ITEM_VERIFIED + "/" + CollectionConst.refundStatus.ITEM_APPROVED + " status.");
                ret = false;
            }
        } else if (RefundPermissionCodePolicy.REFUND_BUTTON_PROCESS === operation) {
            if (CollectionConst.refundStatus.ITEM_APPROVED !== status) {
                messageBarVM.showError("Cannot " + operation + " because the refund claim(s) is not in " + CollectionConst.refundStatus.ITEM_APPROVED + " status.");
                ret = false;
            }
        } else if (RefundPermissionCodePolicy.REFUND_BUTTON_ISSUE === operation) {
            if (CollectionConst.refundStatus.ITEM_PROCESSED !== status) {
                messageBarVM.showError("Cannot " + operation + " because the refund claim(s) is not in " + CollectionConst.refundStatus.ITEM_PROCESSED + " status.");
                ret = false;
            }
        } else if (CollectionConst.refundStatus.ITEM_NEW !== status
            && CollectionConst.refundStatus.ITEM_INPUT !== status
            && CollectionConst.refundStatus.ITEM_PROCESSED !== status) {
            messageBarVM.showError("Record status is " + status + ", cannot edit.");
            ret = false;
        }
        return ret;
    }, [messageBarVM]);

    const checkIsStatusOperable = useCallback((status: string) => {
        return isStatusOperable(null, status);
    }, [isStatusOperable]);

    const handleEdit = useCallback(() => {
        if (currentRow.status && !checkIsStatusOperable(currentRow.status)) {
            return;
        }
        refundVM.onEditClick();
    }, [checkIsStatusOperable, currentRow.status, refundVM]);

    const handleReset = useCallback(() => {
        refundVM.onEditResetClick(isAdd);
    }, [refundVM, isAdd]);


    const isValidFormat = (str: string) => {
        return /^[0-9]{5}-[0-9]{7}-[0-9]$/.test(str);
    };

    const isReadonlyForProcess = useCallback(() => {
        return !((currentEditRow.status === CollectionConst.refundStatus.ITEM_PROCESSED) && isEdit)
    }, [currentEditRow.status, isEdit]);

    const isReadonlyForEdit = useCallback(() => {
        return ((currentEditRow.status === CollectionConst.refundStatus.ITEM_PROCESSED) || (!isAdd && !isEdit))
    }, [currentEditRow.status, isAdd, isEdit]);

    const isReadonlyForPayeeName = useCallback(() => {
        if (operatingUnit !== 'SAPT' && operatingUnit !== 'KICT') {
            return !isAdd;
        } else {
            return true;
        }
    }, [isAdd, operatingUnit]);

    const handleSave = useCallback(async () => {
        // if (!currentEditRow.receiptNo) {
        //     messageBarVM.showError("Receipt No. is mandatory.");
        //     return;
        // }
        // if (!currentEditRow.payeeName && operatingUnit !== 'SAPT' && operatingUnit !== 'KICT') {
        //     messageBarVM.showError("Payee Name is mandatory.");
        //     return;
        // }
        if (currentEditRow.cnic && !(isValidFormat(currentEditRow.cnic))) {
            messageBarVM.showError("Invalid format of CNIC.(Valid format:nnnnn-nnnnnnn-n)");
            return;
        }
        setIsLoading(true);
        refundVM.onSaveClicked();
        await refundVM.onSave(currentEditRow, operatingUnit).then(data => {
            if (data && data.success) {
                refundVM.onSearch(searchCriteria).then((data) => {
                    messageBarVM.showSuccess("Save Successfully.");
                    setIsLoading(false)
                }).catch(error => {
                    setIsLoading(false)
                });
            } else {
                setIsLoading(false)
                messageBarVM.showError(data.data);
                return;
            }
        });
    }, [currentEditRow, refundVM, messageBarVM, searchCriteria, operatingUnit]);

    const handClose = useCallback(() => {
        refundVM.onCloseEditPanel();
    }, [refundVM]);

    const onNumberFieldChange = useCallback((e: any, fieldName: string, fieldValue: any) => {
        refundVM.onEditFieldChange(fieldName, fieldValue);
    }, [refundVM]);

    const memoReceiptNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={!isAdd}
                isShowOptional={false}
                readOnlyValue={currentEditRow.receiptNo ?? ""}
                fieldValue={currentEditRow?.receiptNo ?? ""}
                fieldLabel={SCREEN_CONSTANT.RECEIPT_NO}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'receiptNo'}
                maxLength={11}
                isMultipleDropDown={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onEditFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [allFormState, isAdd, currentEditRow.receiptNo, SCREEN_CONSTANT.RECEIPT_NO, isSaveClicked, refundVM])

    const memoRefundDraftId = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={true}
                isShowOptional={false}
                readOnlyValue={currentEditRow.refundDraftId ?? ""}
                fieldValue={currentEditRow?.refundDraftId ?? ""}
                fieldLabel={SCREEN_CONSTANT.REFUND_DRAFT_ID}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'refundDraftId'}
                maxLength={60}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onEditFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [allFormState, currentEditRow.refundDraftId, SCREEN_CONSTANT.REFUND_DRAFT_ID, isSaveClicked, refundVM])

    const memoRefundFinalId = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={true}
                isShowOptional={false}
                readOnlyValue={currentEditRow.refundFinalId ?? ""}
                fieldValue={currentEditRow?.refundFinalId ?? ""}
                fieldLabel={SCREEN_CONSTANT.REFUND_FINAL_ID}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'refundFinalId'}
                maxLength={60}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onEditFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [allFormState, currentEditRow.refundFinalId, SCREEN_CONSTANT.REFUND_FINAL_ID, isSaveClicked, refundVM])

    const memoCounterCustomerID = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={true}
                isShowOptional={false}
                readOnlyValue={currentEditRow.counterCustomerID ?? ""}
                fieldValue={currentEditRow?.counterCustomerID ?? ""}
                fieldLabel={SCREEN_CONSTANT.COUNTER_CUSTOMER_ID}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'counterCustomerID'}
                maxLength={60}
                isLimitOptions={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onEditFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [allFormState, currentEditRow.counterCustomerID, SCREEN_CONSTANT.COUNTER_CUSTOMER_ID, isSaveClicked, refundVM])

    const memoPayeeName = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={isReadonlyForPayeeName()}
                isShowOptional={false}
                readOnlyValue={currentEditRow.payeeName ?? ""}
                fieldValue={currentEditRow?.payeeName ?? ""}
                fieldLabel={SCREEN_CONSTANT.PAYEE_NAME}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'payeeName'}
                maxLength={150}
                isLimitOptions={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onEditFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [allFormState, isReadonlyForPayeeName, currentEditRow.payeeName, SCREEN_CONSTANT.PAYEE_NAME, isSaveClicked, refundVM])

    const memoPayeeNTN = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={isReadonlyForEdit()}
                isShowOptional={true}
                readOnlyValue={currentEditRow.payeeNTN ?? ""}
                fieldValue={currentEditRow?.payeeNTN ?? ""}
                fieldLabel={SCREEN_CONSTANT.PAYEE_NTN}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'payeeNTN'}
                maxLength={30}
                isLimitOptions={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onEditFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [allFormState, isReadonlyForEdit, currentEditRow.payeeNTN, SCREEN_CONSTANT.PAYEE_NTN, isSaveClicked, refundVM])

    const memoPaymentAmount = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <NumberInputComponent
                label={SCREEN_CONSTANT.PAYMENT_AMOUNT}
                maxLength={10}
                fieldName={"paymentAmount"}
                value={currentEditRow?.paymentAmount ?? ''}
                errorMessage={allFormState ? allFormState['paymentAmount'] ?? '' : ''}
                disabled={true}
                decimalPlaces={2}
                onChange={(e: any, fieldKey: string, fieldValue: any) => onNumberFieldChange(e, fieldKey, fieldValue)}
            />
        </div>
        , [SCREEN_CONSTANT.PAYMENT_AMOUNT, allFormState, currentEditRow?.paymentAmount, onNumberFieldChange])

    const memoPaiedInvoiceAmount = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <NumberInputComponent
                label={SCREEN_CONSTANT.PAIED_INVOICE_AMOUNT}
                maxLength={10}
                fieldName={"paiedInvoiceAmount"}
                value={currentEditRow?.paiedInvoiceAmount ?? ''}
                errorMessage={allFormState ? allFormState['paiedInvoiceAmount'] ?? '' : ''}
                disabled={true}
                decimalPlaces={2}
                onChange={(e: any, fieldKey: string, fieldValue: any) => onNumberFieldChange(e, fieldKey, fieldValue)}
            />
        </div>
        , [SCREEN_CONSTANT.PAIED_INVOICE_AMOUNT, allFormState, currentEditRow?.paiedInvoiceAmount, onNumberFieldChange])

    const memoTotalInvoiceAmount = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <NumberInputComponent
                label={SCREEN_CONSTANT.TOTAL_INVOICE_AMOUNT}
                maxLength={10}
                fieldName={"totalInvoiceAmount"}
                value={currentEditRow?.totalInvoiceAmount ?? ''}
                errorMessage={allFormState ? allFormState['totalInvoiceAmount'] ?? '' : ''}
                disabled={true}
                decimalPlaces={2}
                onChange={(e: any, fieldKey: string, fieldValue: any) => onNumberFieldChange(e, fieldKey, fieldValue)}
            />
        </div>
        , [SCREEN_CONSTANT.TOTAL_INVOICE_AMOUNT, allFormState, currentEditRow?.totalInvoiceAmount, onNumberFieldChange])

    const memoHandlingCharge = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <NumberInputComponent
                label={SCREEN_CONSTANT.HANDLING_CHARGE}
                maxLength={10}
                fieldName={"handlingCharge"}
                value={currentEditRow?.handlingCharge ?? ''}
                errorMessage={allFormState ? allFormState['handlingCharge'] ?? '' : ''}
                disabled={true}
                decimalPlaces={2}
                onChange={(e: any, fieldKey: string, fieldValue: any) => onNumberFieldChange(e, fieldKey, fieldValue)}
            />
        </div>
        , [SCREEN_CONSTANT.HANDLING_CHARGE, allFormState, currentEditRow?.handlingCharge, onNumberFieldChange])

    const memoRefundAmount = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <NumberInputComponent
                label={SCREEN_CONSTANT.REFUND_AMOUNT}
                maxLength={10}
                fieldName={"refundAmount"}
                value={currentEditRow?.refundAmount ?? ''}
                errorMessage={allFormState ? allFormState['refundAmount'] ?? '' : ''}
                disabled={true}
                decimalPlaces={2}
                onChange={(e: any, fieldKey: string, fieldValue: any) => onNumberFieldChange(e, fieldKey, fieldValue)}
            />
        </div>
        , [SCREEN_CONSTANT.REFUND_AMOUNT, allFormState, currentEditRow?.refundAmount, onNumberFieldChange])

    const memoChequeCollectorName = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={isReadonlyForEdit()}
                isShowOptional={true}
                readOnlyValue={currentEditRow.chequeCollectorName ?? ""}
                fieldValue={currentEditRow?.chequeCollectorName ?? ""}
                fieldLabel={SCREEN_CONSTANT.CHEQUE_COLLECTOR_NAME}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'chequeCollectorName'}
                maxLength={50}
                isMultipleDropDown={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onEditFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [allFormState, isReadonlyForEdit, currentEditRow.chequeCollectorName, SCREEN_CONSTANT.CHEQUE_COLLECTOR_NAME, isSaveClicked, refundVM])

    const memoCnic = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={isReadonlyForEdit()}
                isShowOptional={true}
                readOnlyValue={currentEditRow.cnic ?? ""}
                fieldValue={currentEditRow?.cnic ?? ""}
                fieldLabel={SCREEN_CONSTANT.CNIC}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'cnic'}
                maxLength={60}
                isMultipleDropDown={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onEditFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [allFormState, isReadonlyForEdit, currentEditRow.cnic, SCREEN_CONSTANT.CNIC, isSaveClicked, refundVM])

    const memoMobileNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={isReadonlyForEdit()}
                isShowOptional={true}
                readOnlyValue={currentEditRow.mobileNo ?? ""}
                fieldValue={currentEditRow?.mobileNo ?? ""}
                fieldLabel={SCREEN_CONSTANT.MOBILE_NO}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'mobileNo'}
                maxLength={15}
                isMultipleDropDown={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onEditFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [allFormState, isReadonlyForEdit, currentEditRow.mobileNo, SCREEN_CONSTANT.MOBILE_NO, isSaveClicked, refundVM])

    const memoChequeNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={isReadonlyForProcess()}
                isShowOptional={false}
                readOnlyValue={currentEditRow.chequeNo ?? ""}
                fieldValue={currentEditRow?.chequeNo ?? ""}
                fieldLabel={SCREEN_CONSTANT.CHEQUE_NO}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'chequeNo'}
                maxLength={10}
                isMultipleDropDown={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onEditFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [allFormState, isReadonlyForProcess, currentEditRow.chequeNo, SCREEN_CONSTANT.CHEQUE_NO, isSaveClicked, refundVM])

    const memoChequeIssueDate = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <DatePickerComponent
                disabled={isReadonlyForProcess()}
                label={SCREEN_CONSTANT.CHEQUE_ISSUE_DATE}
                width="280px"
                date={currentEditRow?.chequeIssueDate}
                fieldName="chequeIssueDate"
                errorMessage={allFormState ? allFormState['chequeIssueDate'] ?? '' : ''}
                onDateChange={refundVM.onEditFieldChange} />
        </div>
        , [SCREEN_CONSTANT.CHEQUE_ISSUE_DATE, allFormState, currentEditRow?.chequeIssueDate, isReadonlyForProcess, refundVM.onEditFieldChange])

    const memoChequeAmount = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <NumberInputComponent
                label={SCREEN_CONSTANT.CHEQUE_AMOUNT}
                maxLength={10}
                fieldName={"chequeAmount"}
                value={currentEditRow?.chequeAmount ?? ""}
                errorMessage={allFormState ? allFormState['chequeAmount'] ?? '' : ''}
                disabled={true}
                decimalPlaces={2}
                onChange={(e: any, fieldKey: string, fieldValue: any) => onNumberFieldChange(e, fieldKey, fieldValue)}
            />
        </div>
        , [SCREEN_CONSTANT.CHEQUE_AMOUNT, allFormState, currentEditRow?.chequeAmount, onNumberFieldChange])

    const memoCreatedBy = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={true}
                isShowOptional={false}
                readOnlyValue={currentEditRow.createdBy ?? ""}
                fieldValue={currentEditRow?.createdBy ?? ""}
                fieldLabel={SCREEN_CONSTANT.CREATED_BY}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'createdBy'}
                maxLength={60}
                isMultipleDropDown={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onEditFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [allFormState, currentEditRow.createdBy, SCREEN_CONSTANT.CREATED_BY, isSaveClicked, refundVM])

    const memoCreatedDate = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <DatePickerComponent
                disabled={true}
                label={SCREEN_CONSTANT.CREATED_DATE}
                width="280px"
                date={currentEditRow?.createdDate}
                fieldName="createdDate"
                errorMessage={allFormState ? allFormState['createdDate'] ?? '' : ''}
                onDateChange={refundVM.onEditFieldChange} />
        </div>
        , [SCREEN_CONSTANT.CREATED_DATE, allFormState, currentEditRow?.createdDate, refundVM.onEditFieldChange])

    const memoChangedBy = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={true}
                isShowOptional={true}
                readOnlyValue={currentEditRow.changedBy ?? ""}
                fieldValue={currentEditRow?.changedBy ?? ""}
                fieldLabel={SCREEN_CONSTANT.CHANGED_BY}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'changedBy'}
                maxLength={60}
                requiredFieldList={BankerRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onEditFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [allFormState, currentEditRow.changedBy, SCREEN_CONSTANT.CHANGED_BY, isSaveClicked, refundVM])

    const memoChangedDate = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <DatePickerComponent
                disabled={true}
                label={SCREEN_CONSTANT.CHANGED_DATE}
                width="280px"
                date={currentEditRow?.changedDate}
                fieldName="changedDate"
                errorMessage={allFormState ? allFormState['changedDate'] ?? '' : ''}
                onDateChange={refundVM.onEditFieldChange} />
        </div>
        , [SCREEN_CONSTANT.CHANGED_DATE, allFormState, currentEditRow?.changedDate, refundVM.onEditFieldChange])

    const memoVerifiedBy = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={true}
                isShowOptional={true}
                readOnlyValue={currentEditRow.verifiedBy ?? ""}
                fieldValue={currentEditRow?.verifiedBy ?? ""}
                fieldLabel={SCREEN_CONSTANT.VERIFIED_BY}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'verifiedBy'}
                maxLength={60}
                requiredFieldList={BankerRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onEditFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [allFormState, currentEditRow.verifiedBy, SCREEN_CONSTANT.VERIFIED_BY, isSaveClicked, refundVM])

    const memoVerifiedDate = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <DatePickerComponent
                disabled={true}
                label={SCREEN_CONSTANT.VERIFIED_DATE}
                width="280px"
                date={currentEditRow?.verifiedDate}
                fieldName="verifiedDate"
                errorMessage={allFormState ? allFormState['verifiedDate'] ?? '' : ''}
                onDateChange={refundVM.onEditFieldChange} />
        </div>
        , [SCREEN_CONSTANT.VERIFIED_DATE, allFormState, currentEditRow?.verifiedDate, refundVM.onEditFieldChange])

    const memoApprovedBy = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={true}
                isShowOptional={true}
                readOnlyValue={currentEditRow.approvedBy ?? ""}
                fieldValue={currentEditRow?.approvedBy ?? ""}
                fieldLabel={SCREEN_CONSTANT.APPROVED_BY}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'approvedBy'}
                maxLength={60}
                requiredFieldList={BankerRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onEditFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [allFormState, currentEditRow.approvedBy, SCREEN_CONSTANT.APPROVED_BY, isSaveClicked, refundVM])

    const memoApprovedDate = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <DatePickerComponent
                disabled={true}
                label={SCREEN_CONSTANT.APPROVED_DATE}
                width="280px"
                date={currentEditRow?.approvedDate}
                fieldName="approvedDate"
                errorMessage={allFormState ? allFormState['approvedDate'] ?? '' : ''}
                onDateChange={refundVM.onEditFieldChange} />
        </div>
        , [SCREEN_CONSTANT.APPROVED_DATE, allFormState, currentEditRow?.approvedDate, refundVM.onEditFieldChange])

    const memoRefundedBy = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={true}
                isShowOptional={true}
                readOnlyValue={currentEditRow.refundedBy ?? ""}
                fieldValue={currentEditRow?.refundedBy ?? ""}
                fieldLabel={SCREEN_CONSTANT.REFUNDED_BY}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'refundedBy'}
                maxLength={60}
                requiredFieldList={BankerRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onEditFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [allFormState, currentEditRow.refundedBy, SCREEN_CONSTANT.REFUNDED_BY, isSaveClicked, refundVM])

    const memoRefundedDatetime = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <DatePickerComponent
                disabled={true}
                label={SCREEN_CONSTANT.REFUNDED_DATE}
                width="280px"
                date={currentEditRow?.refundedDatetime}
                fieldName="refundedDatetime"
                errorMessage={allFormState ? allFormState['refundedDatetime'] ?? '' : ''}
                onDateChange={refundVM.onEditFieldChange} />
        </div>
        , [SCREEN_CONSTANT.REFUNDED_DATE, allFormState, currentEditRow?.refundedDatetime, refundVM.onEditFieldChange])

    const memoProcessedBy = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={true}
                isShowOptional={true}
                readOnlyValue={currentEditRow.processedBy ?? ""}
                fieldValue={currentEditRow?.processedBy ?? ""}
                fieldLabel={SCREEN_CONSTANT.PROCESSED_BY}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'processedBy'}
                maxLength={60}
                requiredFieldList={BankerRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onEditFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [allFormState, currentEditRow.processedBy, SCREEN_CONSTANT.PROCESSED_BY, isSaveClicked, refundVM])

    const memoProcessedDate = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <DatePickerComponent
                disabled={true}
                label={SCREEN_CONSTANT.PROCESSED_DATE}
                width="280px"
                date={currentEditRow?.processedDate}
                fieldName="processedDate"
                errorMessage={allFormState ? allFormState['processedDate'] ?? '' : ''}
                onDateChange={refundVM.onEditFieldChange} />
        </div>
        , [SCREEN_CONSTANT.PROCESSED_DATE, allFormState, currentEditRow?.processedDate, refundVM.onEditFieldChange])

    const memoIssuedBy = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={true}
                isShowOptional={true}
                readOnlyValue={currentEditRow.issuedBy ?? ""}
                fieldValue={currentEditRow?.issuedBy ?? ""}
                fieldLabel={SCREEN_CONSTANT.ISSUED_BY}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'issuedBy'}
                maxLength={60}
                requiredFieldList={BankerRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onEditFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [allFormState, currentEditRow.issuedBy, SCREEN_CONSTANT.ISSUED_BY, isSaveClicked, refundVM])

    const memoIssuedDate = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <DatePickerComponent
                disabled={true}
                label={SCREEN_CONSTANT.ISSUED_DATE}
                width="280px"
                date={currentEditRow?.issuedDate}
                fieldName="issuedDate"
                errorMessage={allFormState ? allFormState['issuedDate'] ?? '' : ''}
                onDateChange={refundVM.onEditFieldChange} />
        </div>
        , [SCREEN_CONSTANT.ISSUED_DATE, allFormState, currentEditRow?.issuedDate, refundVM.onEditFieldChange])

    const memoRefundType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={true}
                isShowOptional={false}
                readOnlyValue={currentEditRow.refundType ?? ""}
                fieldValue={currentEditRow?.refundType ?? ""}
                fieldLabel={SCREEN_CONSTANT.REFUND_TYPE}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'refundType'}
                maxLength={60}
                isMultipleDropDown={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onEditFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [allFormState, currentEditRow.refundType, SCREEN_CONSTANT.REFUND_TYPE, isSaveClicked, refundVM])

    const memoStatus = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={true}
                isShowOptional={false}
                readOnlyValue={currentEditRow.status ?? ""}
                fieldValue={currentEditRow?.status ?? ""}
                fieldLabel={SCREEN_CONSTANT.STATUS}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'status'}
                maxLength={60}
                isMultipleDropDown={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onEditFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [allFormState, currentEditRow.status, SCREEN_CONSTANT.STATUS, isSaveClicked, refundVM])

    const memoRemark = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={true}
                isShowOptional={false}
                readOnlyValue={currentEditRow.remark ?? ""}
                fieldValue={currentEditRow?.remark ?? ""}
                fieldLabel={SCREEN_CONSTANT.REMARK}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'remark'}
                maxLength={60}
                isMultipleDropDown={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onEditFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [allFormState, currentEditRow.remark, SCREEN_CONSTANT.REMARK, isSaveClicked, refundVM])

    const memoPayOrderNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={true}
                isShowOptional={false}
                readOnlyValue={currentEditRow.payOrderNo ?? ""}
                fieldValue={currentEditRow?.payOrderNo ?? ""}
                fieldLabel={SCREEN_CONSTANT.PAY_ORDER_NO}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'payOrderNo'}
                maxLength={60}
                isMultipleDropDown={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onEditFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [allFormState, currentEditRow.payOrderNo, SCREEN_CONSTANT.PAY_ORDER_NO, isSaveClicked, refundVM])

    const memoBankerCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={true}
                isShowOptional={false}
                readOnlyValue={currentEditRow.bankCode ?? ""}
                fieldValue={currentEditRow?.bankCode ?? ""}
                fieldLabel={SCREEN_CONSTANT.BANK_CODE}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'bankCode'}
                maxLength={60}
                isMultipleDropDown={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onEditFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [allFormState, currentEditRow.bankCode, SCREEN_CONSTANT.BANK_CODE, isSaveClicked, refundVM])

    const memoAgentName = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "450px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={true}
                isShowOptional={false}
                readOnlyValue={currentEditRow.agentName ?? ""}
                fieldValue={currentEditRow?.agentName ?? ""}
                fieldLabel={SCREEN_CONSTANT.AGENT_NAME}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'agentName'}
                maxLength={60}
                isMultipleDropDown={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onEditFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [allFormState, currentEditRow.agentName, SCREEN_CONSTANT.AGENT_NAME, isSaveClicked, refundVM])

    return <>
        <StyledSidebar style={{ width: '100%', height: "100%", border: 'none', backgroundColor: '#FFFFFF', overflow: 'auto', padding: '0' }} position='right'>
            <Sidebarheader style={{ width: '100%' }}>
                {isLoading && <Loader Indicator="Spinner" size="Medium" />}
                <Breadcrumb>
                    <HPHBreadcrumb breadcrumbData={[{ title: SCREEN_CONSTANT.CATEGORY },
                    { title: SCREEN_CONSTANT.TITLE }]} onCurrentClick={() => { }}>
                    </HPHBreadcrumb>
                </Breadcrumb>
                <StyledAction>
                    <>
                        {allowUpdate && (!(isAdd || isEdit)) && <NbisIconButton fileName='Icon-pen' size='medium' disabled={false} toolTipArrow={false} toolTipPlacement="bottom" toolTipText={'Edit'} onClick={handleEdit} />}
                        {(isAdd || isEdit) && <IconButton fileName="Icon-reset" size="medium" disabled={false} toolTipArrow={false} toolTipPlacement="bottom" toolTipText={'Reset'} onClick={() => handleReset()} />}

                        <IconButton fileName="Icon-cross" size="medium" disabled={false} toolTipArrow={false} toolTipPlacement='bottom' toolTipText={'Close'} onClick={() => handClose()} />
                        {(isAdd || isEdit) && <IconButton fileName="Icon-tick" size="medium" disabled={false} toolTipArrow={false} toolTipPlacement="bottom" toolTipText={'Save'} onClick={() => handleSave()} />}
                    </>
                </StyledAction>
            </Sidebarheader>

            <div className={'add-edit-form'} style={{ height: 'calc(100vh - 125px)', maxHeight: 'calc(100vh - 125px)', overflow: 'auto' }}>
                <div className="flex-row-auto">
                    <div className={'flex-row-item'}>
                        <div className={'im-flex-row'}>
                            <div className={'flex-row-item-stretch'}>
                                <SidebarTitle>
                                    {isAdd && "New Refund"}
                                    {!isAdd && currentEditRow.receiptNo}
                                </SidebarTitle>
                                {<SidebarCaption>"All fields are required unless indicated as 'optional'"</SidebarCaption>}
                            </div>
                        </div>

                        <CriteriaItemContainer>
                            {memoReceiptNo}
                            {memoRefundDraftId}
                            {memoRefundFinalId}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoCounterCustomerID}
                            {memoPayeeName}
                            {memoPayeeNTN}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoPaymentAmount}
                            {(operatingUnit === "SAPT" || operatingUnit === "KICT") ? memoPaiedInvoiceAmount : memoRemark}
                            {memoTotalInvoiceAmount}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoHandlingCharge}
                            {memoRefundAmount}
                            {memoChequeCollectorName}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoCnic}
                            {memoMobileNo}
                            {memoChequeNo}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoChequeIssueDate}
                            {memoChequeAmount}
                            {memoCreatedBy}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoCreatedDate}
                            {memoChangedBy}
                            {memoChangedDate}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoVerifiedBy}
                            {memoVerifiedDate}
                            {memoApprovedBy}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoApprovedDate}
                            {memoRefundedBy}
                            {memoRefundedDatetime}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoProcessedBy}
                            {memoProcessedDate}
                            {memoIssuedBy}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoIssuedDate}
                            {memoRefundType}
                            {memoStatus}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {(operatingUnit === "SAPT" || operatingUnit === "KICT") && memoRemark}
                            {(operatingUnit === "SAPT" || operatingUnit === "KICT") && memoPayOrderNo}
                            {(operatingUnit === "SAPT" || operatingUnit === "KICT") && memoBankerCode}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {(operatingUnit === "SAPT" || operatingUnit === "KICT") && memoAgentName}
                        </CriteriaItemContainer>


                    </div>
                </div>
            </div>
        </StyledSidebar>
    </>
}

export default RefundEditPanel;
